import axios from 'axios';
import { getAPIHost, getExpressAPIHost } from '../../utils';
import Split from '../split';

/**
 * Get a signed URL for an S3 file.
 *
 * @param {string} key - S3 key (or full S3 URL) to sign.
 * @param {boolean} isDownload - If true, set Content-Disposition: attachment.
 * @param {boolean} useLegacyBucketSigner - If true, sign against the legacy
 *   ecs-storage bucket only (no dual-read). Use this ONLY for files known to
 *   be in ecs-storage. For all other files, omit this arg — the split-gated
 *   normal path (/s3/get-signed-url/:key) does dual-read (checks ecs-storage
 *   first, falls back to attachments bucket).
 * @param {string|null} fileName - Override the download filename.
 * @param {boolean} forceExpress - If true, always use the Express
 *   /s3/get-signed-url endpoint (dual-read) regardless of the
 *   use_express_migration_endpoints split. Use for files that are known to
 *   reside in the attachments bucket (e.g. delivery reports generated by
 *   Express).
 */
const getFileURL = async (key, isDownload = false, useLegacyBucketSigner = false, fileName = null, forceExpress = false) => {
    let filepath = key;
    try {
        // If the URI is already signed then keep original
        if (filepath.includes('X-Amz-Signature')) {
            return filepath;
        }
        // If a full S3 URL is passed rather than path then extract the file path
        if (filepath.includes('amazonaws.com')) {
            filepath = new URL(key).pathname.substring(1);
        }

        // Build optional fileName query param for download filename override.
        // Express /s3/get-signed-url/:key accepts ?fileName=... to set the
        // Content-Disposition filename (used by shared files where the S3
        // key is a hash but the download should use the original name).
        const fileNameParam = fileName ? `&fileName=${encodeURIComponent(fileName)}` : '';

        if (useLegacyBucketSigner) {
            // PFM-2761 followup: use the legacy-bucket Express endpoint that signs
            // against process.env.AWS_BUCKET (the ${account_name}-mm-ecs-storage bucket
            // used by storage-s3.service.ts in the api/LoopBack repo). The previous
            // /reports/get-signed-url endpoint signed against a different bucket and
            // returned URLs that violated CSP for fuel intake image previews.
            // See PR #3635 / PR #2217.
            const res = await axios.get(`${getExpressAPIHost()}/s3/get-signed-url-legacy/${encodeURIComponent(filepath)}?isDownload=${isDownload}${fileNameParam}`);
            // Response is a JSON-quoted string (text/plain) — parse to plain URL.
            let url = res.data;
            if (typeof url === 'string') {
                try {
                    return JSON.parse(url);
                } catch (e) {
                    return url;
                }
            }
            return url?.s3Url ?? url?.signedUrl ?? url;
        }

        // Split-gate: when use_express_migration_endpoints is ON, hit the Express
        // GET /s3/get-signed-url/:key endpoint. When OFF, fall back to LoopBack.
        // forceExpress bypasses the split for files known to be in the
        // attachments bucket (e.g. delivery reports uploaded by Express).
        const useExpress = forceExpress || Split.getBooleanTreatment('use_express_migration_endpoints');
        const host = useExpress ? getExpressAPIHost() : getAPIHost();
        const endpoint = useExpress ? `${host}/s3/get-signed-url/${encodeURIComponent(filepath)}?isDownload=${isDownload}${fileNameParam}` : `${host}/get-signed-url/${encodeURIComponent(filepath)}?isDownload=${isDownload}`;
        const { data } = await axios.get(endpoint);
        // Normalize: Express returns a JSON-quoted string (text/plain), LoopBack may
        // return a plain string or object. Extract a plain URL string in all cases.
        if (typeof data === 'string') {
            // Express returns JSON.stringify(url) as text/plain — strip quotes if present
            try {
                return JSON.parse(data);
            } catch {
                return data;
            }
        }
        return data?.signedUrl ?? data?.s3Url ?? data;
    } catch (e) {
        throw new Error('Unable to find file. Please contact support.');
    }
};
export default getFileURL;
